import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleAnalyticsEvents } from '../../../app/shared/models/google-analytics-events.enum';
import { GoogleAnalyticsService } from '../../../app/shared/services/google-analytics.service';

export interface LocalCartItem {
  photoId: string;
  eventId: string;
  eventName: string;
  price: number;
  addedAt: string; // ISO string
  expiresAt: string; // ISO string
}

export interface LocalCartData {
  items: LocalCartItem[];
  lastUpdated: string;
}

@Injectable({
  providedIn: 'root',
})
export class LocalCartService {
  private readonly CART_STORAGE_KEY = 'boatmu_gallery_cart';
  private readonly CART_EXPIRY_DAYS = 7;

  private cartSubject = new BehaviorSubject<LocalCartItem[]>([]);

  constructor(private readonly googleAnalyticsService: GoogleAnalyticsService) {
    this.initializeCart();
    // Clean up expired items on service init
    this.cleanupExpiredItems();
  }

  /**
   * Get cart items as observable
   */
  get cartItems$(): Observable<LocalCartItem[]> {
    return this.cartSubject.asObservable();
  }

  /**
   * Get current cart items
   */
  get cartItems(): LocalCartItem[] {
    return this.cartSubject.value;
  }

  /**
   * Get cart count
   */
  get cartCount(): number {
    return this.cartItems.length;
  }

  /**
   * Get total cart value
   */
  get cartTotal(): number {
    return this.cartItems.reduce((total, item) => total + item.price, 0);
  }

  /**
   * Add item to cart
   */
  addToCart(item: { photoId: string; eventId: string; eventName: string; price: number }): void {
    const currentItems = this.cartItems;

    // Check if item already exists
    if (currentItems.some(cartItem => cartItem.photoId === item.photoId)) {
      console.warn('Item already in cart:', item.photoId);
      return;
    }

    const now = new Date();
    const expiresAt = new Date(now.getTime() + this.CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

    const cartItem: LocalCartItem = {
      ...item,
      addedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    };

    const updatedItems = [...currentItems, cartItem];
    this.updateCart(updatedItems);

    // Track successful add to cart
    this.googleAnalyticsService.trackEventWithDimensions(GoogleAnalyticsEvents.ADD_TO_CART, {
      photo_id: item.photoId,
      event_id: item.eventId,
      event_name: item.eventName,
      price: item.price,
      cart_total: this.cartTotal,
      cart_item_count: this.cartCount,
    });
  }

  /**
   * Remove item from cart
   */
  removeFromCart(photoId: string): void {
    const currentItems = this.cartItems;
    const itemToRemove = currentItems.find(item => item.photoId === photoId);

    if (!itemToRemove) {
      console.warn('Item not found in cart:', photoId);
      return;
    }

    const updatedItems = currentItems.filter(item => item.photoId !== photoId);
    const oldTotal = this.cartTotal;
    const oldCount = this.cartCount;

    this.updateCart(updatedItems);

    // Track item removal
    this.googleAnalyticsService.trackEventWithDimensions(GoogleAnalyticsEvents.REMOVE_FROM_CART, {
      photo_id: photoId,
      event_id: itemToRemove.eventId,
      event_name: itemToRemove.eventName,
      price: itemToRemove.price,
      cart_total_before: oldTotal,
      cart_total_after: this.cartTotal,
      cart_item_count_before: oldCount,
      cart_item_count_after: this.cartCount,
      time_in_cart_minutes: this.getTimeInCartMinutes(itemToRemove.addedAt),
    });
  }

  private getTimeInCartMinutes(addedAt: string): number {
    const added = new Date(addedAt);
    const now = new Date();
    return Math.round((now.getTime() - added.getTime()) / (1000 * 60));
  }

  /**
   * Clear entire cart
   */
  clearCart(): void {
    this.updateCart([]);
  }

  /**
   * Check if photo is in cart
   */
  isInCart(photoId: string): boolean {
    return this.cartItems.some(item => item.photoId === photoId);
  }

  /**
   * Get expired items (for display purposes)
   */
  getExpiredItems(): LocalCartItem[] {
    const now = new Date();
    return this.cartItems.filter(item => new Date(item.expiresAt) < now);
  }

  /**
   * Clean up expired items automatically
   */
  cleanupExpiredItems(): void {
    const now = new Date();
    const validItems = this.cartItems.filter(item => new Date(item.expiresAt) >= now);

    if (validItems.length !== this.cartItems.length) {
      const expiredCount = this.cartItems.length - validItems.length;
      console.log(`Removed ${expiredCount} expired items from cart`);
      this.updateCart(validItems);
    }
  }

  /**
   * Get photo IDs for API calls
   */
  getPhotoIds(): string[] {
    return this.cartItems.map(item => item.photoId);
  }

  /**
   * Get addedAt map for API calls
   */
  getAddedAtMap(): Record<string, string> {
    const map: Record<string, string> = {};
    this.cartItems.forEach(item => {
      map[item.photoId] = item.addedAt;
    });
    return map;
  }

  /**
   * Update cart items with fresh data from API
   */
  updateCartItemsFromAPI(validItems: LocalCartItem[], invalidPhotoIds: string[]): void {
    // Remove invalid items
    const currentItems = this.cartItems.filter(item => !invalidPhotoIds.includes(item.photoId));

    // Update with fresh data while preserving addedAt and expiresAt
    const updatedItems = currentItems.map(currentItem => {
      const freshItem = validItems.find(valid => valid.photoId === currentItem.photoId);
      if (freshItem) {
        return {
          ...freshItem,
          addedAt: currentItem.addedAt,
          expiresAt: currentItem.expiresAt,
        };
      }
      return currentItem;
    });

    this.updateCart(updatedItems);
  }

  /**
   * Get expiry message for UI
   */
  getExpiryMessage(): string {
    return `Items in cart will be automatically removed after ${this.CART_EXPIRY_DAYS} days.`;
  }

  /**
   * Initialize cart from localStorage
   */
  private initializeCart(): void {
    try {
      const stored = localStorage.getItem(this.CART_STORAGE_KEY);
      if (stored) {
        const cartData: LocalCartData = JSON.parse(stored);
        this.cartSubject.next(cartData.items || []);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      this.cartSubject.next([]);
    }
  }

  /**
   * Update cart in memory and localStorage
   */
  private updateCart(items: LocalCartItem[]): void {
    this.cartSubject.next(items);
    this.saveToStorage(items);
  }

  /**
   * Save cart to localStorage
   */
  private saveToStorage(items: LocalCartItem[]): void {
    try {
      const cartData: LocalCartData = {
        items,
        lastUpdated: new Date().toISOString(),
      };
      localStorage.setItem(this.CART_STORAGE_KEY, JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }
}
